import { useSearchParams } from "react-router";

import { useArticlePagination } from "~/hooks/learning-center/use-article-pagination";

import { ArticleTile } from "../article-card/article-tile";
import { PaginatedGridDisplay } from "./paginated-grid-display";
import { useArticleGridSeed } from "./ssr-seed-context";
import type { ArticleGridProps } from "./types";

/**
 * SFCC-11498 (ARTICLES-12) — port of composable `dynamicGrid.jsx`.
 *
 * API-driven mode: reads the `?topic=` slug from the URL and loads the filtered,
 * published-only tile list via the `/resources/article-grid` resource route
 * (`useArticlePagination`), then paginates client-side. Tiles are rendered
 * directly from DTOs (no per-card reference resolution needed).
 *
 * SFCC-11724: when the articles-hub route provides an SSR seed for the default
 * (no-topic) list, the unfiltered grid renders it on first paint — no cold
 * skeleton. A `?topic=` view still fetches client-side.
 */

const LOADING_SKELETON_COUNT = 8;

export function DynamicGrid({
  itemsToShowPerLoad,
  attributes,
}: ArticleGridProps) {
  const [searchParams] = useSearchParams();
  const topic = searchParams.get("topic") ?? undefined;
  const seed = useArticleGridSeed();

  const {
    displayedItems,
    displayedCount,
    totalCount,
    showNextEntries,
    showAll,
    allEntriesDisplayed,
    isLoading,
    error,
  } = useArticlePagination(topic, itemsToShowPerLoad, seed?.items);

  const { className, ...rest } = (attributes ?? {}) as {
    className?: string;
    [key: string]: unknown;
  };

  if (isLoading) {
    return (
      <div
        className={className}
        {...rest}
        aria-busy="true"
        data-testid="dynamicGrid-Loading"
      >
        <div className="mx-auto grid auto-rows-auto grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3 md:gap-6 xl:grid-cols-4">
          {Array.from({ length: LOADING_SKELETON_COUNT }).map((_, i) => (
            <div
              key={i}
              className="h-[18.75rem] w-full animate-pulse rounded-lg bg-black/10 sm:h-[25rem]"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className} {...rest} data-testid="dynamicGrid-Error" />
    );
  }

  const blocks = displayedItems.map(tile => (
    <ArticleTile
      key={tile.slug}
      image={tile.image}
      title={tile.title}
      url={tile.url}
      ctaText="Read Article"
    />
  ));

  return (
    <PaginatedGridDisplay
      items={blocks}
      itemsText={`Showing ${displayedCount} of ${totalCount} articles`}
      onShowMore={showNextEntries}
      onShowAll={showAll}
      allEntriesDisplayed={allEntriesDisplayed}
      attributes={attributes}
    />
  );
}

export default DynamicGrid;
